import type { Match } from '@uefadigital/panenka';
import { PkEmptyMessage } from '@uefadigital/panenka-react';
import { Fragment, useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import { useGetMatchStatisticQuery } from 'api/fspApi';
import { StatsUnit } from 'components/StatsUnit';
import type { MatchStatistic } from 'types/ApiResponseInterfaces';
import { translateMatchStatName } from 'utils/translateStatName';

const KEY_STATS = [
  'ball_possession',
  'attacks',
  'attempts',
  'attempts_on_target',
  'attempts_off_target',
  'attempts_blocked',
  'passes_accuracy',
  'passes_completed',
  'cross_completed',
  'recovered_ball',
  'tackles',
  'fouls_committed',
  'offsides',
  'corners',
  'yellow_cards',
  'red_cards',
];

const ATTACKING = [
  'goals',
  'goals_scored_inside_penalty_area',
  'goals_scored_outside_penalty_area',
  'attempts',
  'attempts_on_target',
  'attempts_off_target',
  'attempts_blocked',
  'attempts_on_woodwork',
  'attempts_on_bar',
  'attempts_on_post',
  'attempts_on_target_outside_penalty_area',
  'attempts_off_target_outside_penalty_area',
  'assists',
  'penalty_scored',
  'penalty_miss',
  'penalty_awarded',
  'attacks',
  'big_chance',
  'corners',
  'offsides',
  'dribbling',
  'run_solo_into_attacking_third',
  'run_solo_into_key_play_area',
  'run_solo_into_penalty_area',
];

const DISTRIBUTION = [
  'ball_possession',
  'passes_accuracy',
  'passes_completed',
  'passes_attempted',
  'passes_short_completed',
  'passes_medium_completed',
  'passes_long_completed',
  'passes_completed_backward',
  'passes_completed_to_left',
  'passes_completed_to_right',
  'free_kick',
  'delivery_into_attacking_third',
  'delivery_into_key_play_area',
  'delivery_into_penalty_area',
  'cross_accuracy',
  'cross_completed',
  'cross_attempted',
];

const DEFENDING = [
  'recovered_ball',
  'blocked',
  'penalty_conceded',
  'tackles',
  'tackles_won',
  'clearance_completed',
  'clearance_attempted',
];

const GOALKEEPING = [
  'goals_conceded',
  'own_goal_conceded',
  'clean_sheet',
  'saves',
  'saves_on_direct_free_kick',
  'saves_on_indirect_free_kick',
  'saves_on_penalty',
  'claims',
  'claims_high',
  'claims_low',
  'punches',
];

const DISCIPLINARY = [
  'yellow_cards',
  'red_cards',
  'fouls_committed',
  'fouls_committed_in_defensive_third',
  'fouls_committed_in_own_half',
];

interface NormalizedStatistic {
  [key: string]: Record<string, string>;
}

interface IMatchStats {
  matchData: Match;
  isDesktop: boolean;
  isPDFPrint?: boolean;
}

const complexObject = (matchData: Match, statsArr: Array<string>, matchStatistic: Array<MatchStatistic> | undefined) =>
  matchStatistic
    ? statsArr.reduce((acc: NormalizedStatistic, el) => {
        const homeTeamArr =
          matchStatistic[0]?.teamId === matchData?.homeTeam?.id ? matchStatistic[0] : matchStatistic[1];
        const awayTeamArr =
          matchStatistic[1]?.teamId === matchData?.awayTeam?.id ? matchStatistic[1] : matchStatistic[0];

        const awayTeam = awayTeamArr?.statistics?.find((item) => item.name === el);
        const homeTeam = homeTeamArr?.statistics?.find((item) => item.name === el);

        if (Number(homeTeam?.value) === 0 && Number(awayTeam?.value) === 0) {
          return acc;
        }

        if (homeTeam && awayTeam) {
          const key = translateMatchStatName(homeTeam.name);

          acc[key] = {
            homeTeam: homeTeam.value,
            awayTeam: awayTeam.value,
          };
        }

        return acc;
      }, {})
    : {};

export const MatchStats = ({ matchData, isDesktop, isPDFPrint = false }: IMatchStats) => {
  const { t } = useTranslation();

  const titleStyle = isDesktop
    ? 'pk-font-size--xl3 pk-line-height--sm pk-font-style--normal'
    : 'pk-font-size--xl pk-line-height--md pk-font-style--normal';

  const {
    data: matchStatsData,
    isLoading: matchStatsDataLoading,
    isError: matchStatsDataError,
  } = useGetMatchStatisticQuery(matchData.id);

  const keyStatsObj = useMemo(() => complexObject(matchData, KEY_STATS, matchStatsData), [matchData, matchStatsData]);
  const attackStatsObj = useMemo(
    () => complexObject(matchData, ATTACKING, matchStatsData),
    [matchData, matchStatsData],
  );
  const distributionStatsObj = useMemo(
    () => complexObject(matchData, DISTRIBUTION, matchStatsData),
    [matchData, matchStatsData],
  );
  const defendStatsObj = useMemo(
    () => complexObject(matchData, DEFENDING, matchStatsData),
    [matchData, matchStatsData],
  );
  const goalkeeperStatsObj = useMemo(
    () => complexObject(matchData, GOALKEEPING, matchStatsData),
    [matchData, matchStatsData],
  );
  const disciplinaryStatsObj = useMemo(
    () => complexObject(matchData, DISCIPLINARY, matchStatsData),
    [matchData, matchStatsData],
  );

  const isStatistic = useMemo(() => {
    const arrayOfStatisticObjs = [
      keyStatsObj,
      attackStatsObj,
      distributionStatsObj,
      defendStatsObj,
      goalkeeperStatsObj,
      disciplinaryStatsObj,
    ];

    return arrayOfStatisticObjs.every((statistic) => Object.entries(statistic).length);
  }, [keyStatsObj, attackStatsObj, distributionStatsObj, defendStatsObj, goalkeeperStatsObj, disciplinaryStatsObj]);

  if (matchStatsData === undefined || matchStatsDataLoading || matchStatsDataError || (!isStatistic && isPDFPrint)) {
    return <div />;
  }

  if (isStatistic && isPDFPrint) {
    return (
      <div className="pr-avoid-break--inside">
        <div className={`${titleStyle} pk-m--0 pk-text--text-01 pk-mb--xl pr-font-size--18 pk-font--comp-bold`}>
          {t('match_stats')}
        </div>
        <StatsUnit
          statistic={keyStatsObj}
          expand={isPDFPrint}
          heading={t('key_stats')}
          isDesktop={isDesktop}
          isPDFPrint={isPDFPrint}
        />
        <div className="pr-page-break--before" />
        <StatsUnit
          statistic={attackStatsObj}
          expand={isPDFPrint}
          heading={t('attacking')}
          isDesktop={isDesktop}
          isPDFPrint={isPDFPrint}
        />
        <div className="pr-page-break--before" />
        <StatsUnit
          statistic={distributionStatsObj}
          expand={isPDFPrint}
          heading={t('distribution')}
          isDesktop={isDesktop}
          isPDFPrint={isPDFPrint}
        />
        <div className="pr-page-break--before" />
        <StatsUnit
          statistic={defendStatsObj}
          expand={isPDFPrint}
          heading={t('defending')}
          isDesktop={isDesktop}
          isPDFPrint={isPDFPrint}
        />
        <StatsUnit
          statistic={goalkeeperStatsObj}
          expand={isPDFPrint}
          heading={t('goalkeeping')}
          isDesktop={isDesktop}
          isPDFPrint={isPDFPrint}
        />
        <StatsUnit
          statistic={disciplinaryStatsObj}
          expand={isPDFPrint}
          heading={t('disciplinary')}
          isDesktop={isDesktop}
          isPDFPrint={isPDFPrint}
        />
      </div>
    );
  }

  return (
    <>
      <div className={`${titleStyle} pk-text--text-01 pk-mb--l pk-mb-md--xl pr-font-size--18 pk-font--comp-bold`}>
        {t('match_stats')}
      </div>
      {isStatistic ? (
        <Fragment>
          <StatsUnit statistic={keyStatsObj} expand={true} heading={t('key_stats')} isDesktop={isDesktop} />
          <StatsUnit statistic={attackStatsObj} expand={false} heading={t('attacking')} isDesktop={isDesktop} />
          <StatsUnit
            statistic={distributionStatsObj}
            expand={false}
            heading={t('distribution')}
            isDesktop={isDesktop}
          />
          <StatsUnit statistic={defendStatsObj} expand={false} heading={t('defending')} isDesktop={isDesktop} />
          <StatsUnit statistic={goalkeeperStatsObj} expand={false} heading={t('goalkeeping')} isDesktop={isDesktop} />
          <StatsUnit
            statistic={disciplinaryStatsObj}
            expand={false}
            heading={t('disciplinary')}
            isDesktop={isDesktop}
          />
        </Fragment>
      ) : (
        <PkEmptyMessage>{t('official_statistics_not_yet_available')}</PkEmptyMessage>
      )}
    </>
  );
};
